import { api } from "./api";
import {
  TAIResponse,
  TRequestAI,
   ChatMessage,
  ConversationTab,
  IConversationTab,
  DisplayMessage,
  TClinicalSummaryResponse,
  TUpdateClinicalSummaryRequest,
  TUpdateClinicalSummaryResponse,
  TClinicalSummaryHistoryResponse
} from "../types/ai.types";
import { TOption } from "../types/utils.types";
import { tWithKeyAttr as t } from "../../i18nHelper";

export type { ChatMessage, ConversationTab };

export const aiApi = api.injectEndpoints({
  endpoints: (build) => ({
    getResponseFromAI: build.mutation<
      TAIResponse,
      TRequestAI & { conversationId?: number | null }
    >({
      query: ({ conversationId, roleId, typeId, ...body }) => ({
        url: `ai/AI_GetResponseFromAI/${conversationId}/${roleId}/${typeId}`,
        method: "POST",
        body: { message: body.Message },
      }),

      async onQueryStarted(
        { conversationId, Message },
        { dispatch, queryFulfilled }
      ) {
        if (!Message) {
          return;
        }
        if (!conversationId) {
          try {
            await queryFulfilled;
            dispatch(aiApi.util.invalidateTags(["Conversations"]));
          } catch {}
          return;
        }

        const patchResult = dispatch(
          aiApi.util.updateQueryData(
            "getConversationHistory",
            conversationId,
            (draft: DisplayMessage[]) => {
              const userMessage: DisplayMessage = {
                role: "user",
                content: Message,
                date: new Date().toISOString(),
                time: "",
              };
              draft.push(userMessage);
            }
          )
        );

        try {
          const { data } = await queryFulfilled;
          
          // Add AI's reply to the cache
          dispatch(
            aiApi.util.updateQueryData(
              "getConversationHistory",
              conversationId,
              (draft: DisplayMessage[]) => {
                const aiMessage: DisplayMessage = {
                  role: "assistant",
                  content: data.reply,
                  date: new Date().toISOString(),
                  time: "",
                };
                draft.push(aiMessage);
              }
            )
          );
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (result, error, { conversationId }) =>
        conversationId
          ? [
              { type: "ConversationHistory", id: conversationId },
              "Conversations",
            ]
          : ["Conversations"],
    }),
    getResponseFromVoiceAI: build.mutation<
      TAIResponse,
      TRequestAI & { conversationId?: number | null }
    >({
      query: ({ conversationId, roleId, typeId, ...body }) => ({
        url: `ai/AI_GetResponseFromVoiceAI/${conversationId}/${roleId}/${typeId}`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { conversationId }) => {
        const tags: Array<
          { type: "ConversationHistory"; id: number } | "Conversations"
        > = ["Conversations"];
        if (conversationId) {
          tags.push({ type: "ConversationHistory", id: conversationId });
        }
        return tags;
      },
    }),

    getAllConversations: build.query<IConversationTab[], void>({
      query: () => `AI/AI_GetAllConversations`,
      //providesTags: ["Conversations"],
    }),

    getConversationHistory: build.query<DisplayMessage[], number>({
      query: (id) => `AI/AI_GetConversationHistory/${id}`,
      providesTags: (result, error, id) => [
        { type: "ConversationHistory", id },
      ],
      transformResponse: (response: ChatMessage[]) => {
        const filteredResponse = response.filter(
          (message) => message.role !== "system"
        );
        return filteredResponse.map(
          (message): DisplayMessage => ({
            role: message.role as "user" | "assistant",
            content: message.content,
            date: "",
            time: "",
          })
        );
      },
    }),
    getAiFeaturesForSelect: build.query<TOption[], void>({
      query: () => `AI/AI_GetAllConversations`,
      providesTags: ["Features"],

      transformResponse: (response: any[]) => {
        const options: TOption[] = [{ value: "", label: t('WITHOUT_FEATURE') }]; 

        response.forEach((feature) => {
          options.push({
            value: feature.id.toString(),
            label: feature.name,
          });
        });

        return options;
      },
    }),
  
    
    getClinicalSummaryFromAI: build.query<TClinicalSummaryResponse, { usersGuidId: string; lang?: string }>({
      query: ({ usersGuidId, lang }) => {
        return {
          url: `user/getClinicalSummary/${usersGuidId}`,
          method: "GET",
        };
      },
    }),
    updateClinicalSummaryWithTherapistResponse: build.mutation<
      TUpdateClinicalSummaryResponse,
      TUpdateClinicalSummaryRequest
    >({
      query: (body) => ({
        url: `AI/updateClinicalSummaryWithTherapistResponse`,
        method: "POST",
        body,
      }),
      async onQueryStarted({ userGuid, language }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Refetch the clinical summary history query to get updated data
          dispatch(
            aiApi.endpoints.getClinicalSummaryHistory.initiate(
              userGuid,
              { forceRefetch: true }
            )
          );
        } catch {}
      },
    }),
    getClinicalSummaryHistory: build.query<TClinicalSummaryHistoryResponse, string>({
      query: (userGuid) => ({
        url: `user/getClinicalSummaryHistory/${userGuid}`,
        method: "GET",
      }),
    }),
    getClinicalSummaryByVersion: build.query<TClinicalSummaryResponse, { userGuid: string; version: number }>({
      query: ({ userGuid, version }) => ({
        url: `user/getClinicalSummaryByVersion/${userGuid}/${version}`,
        method: "GET",
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetResponseFromAIMutation,
  useGetResponseFromVoiceAIMutation,
  useGetAllConversationsQuery,
  useLazyGetAllConversationsQuery,
  useGetConversationHistoryQuery,
  useGetAiFeaturesForSelectQuery,
  useGetClinicalSummaryFromAIQuery,
  useUpdateClinicalSummaryWithTherapistResponseMutation,
  useGetClinicalSummaryHistoryQuery,
  useLazyGetClinicalSummaryHistoryQuery,
  useGetClinicalSummaryByVersionQuery,
  useLazyGetClinicalSummaryByVersionQuery
} = aiApi;
